import { Directive, inject, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { subjectsComplete } from 'src/app/common/app.helpers';
import {
  TableCellOnActionEvent,
  TableConstructorData,
  TableConstructorDataObject,
} from 'src/app/interfaces/tables.interfaces';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import {
  PaginatablePendingUserInvolvements,
  PendingUserInvolvement,
  PendingUserInvolvementInCommunity,
  PendingUserInvolvementInProject,
} from 'src/app/swagger-api';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';
import { TableCreatorFactory } from 'src/app/views/table-users-list/adapters/table-creator.method';
import { DefaultService as RestApiService } from '../../../swagger-api/api/default.service';

@Directive()
export abstract class SocialInitiativeShared implements OnDestroy {
  protected tableDataChanges$: Subject<TableConstructorData> = new Subject();
  protected resolverChanges$: Subject<PaginatablePendingUserInvolvements> = new Subject();

  protected userRequests: PendingUserInvolvement[] = [];
  protected userPersonalInvites: PendingUserInvolvementInCommunity[] = [];
  protected userProjectInvites: PendingUserInvolvementInProject[] = [];
  protected invites: PendingUserInvolvement[] = [];

  protected apiService = inject(RestApiService);
  protected notificationService = inject(NotificationService);
  protected modalAttributesService = inject(NotificationContextAttributesService);

  public get tableDataChanged(): Observable<TableConstructorData> {
    return this.tableDataChanges$.asObservable();
  }

  public get resolverChanged(): Observable<PaginatablePendingUserInvolvements> {
    return this.resolverChanges$.asObservable();
  }

  public ngOnDestroy(): void {
    subjectsComplete([this.tableDataChanges$, this.resolverChanges$]);
  }

  protected abstract onConfirmRequestOrInvite(event: TableCellOnActionEvent): void;
  protected abstract onRejectRequestOrInvite(event: TableCellOnActionEvent): void;
  protected abstract onRemoveInvite(event: TableCellOnActionEvent): void;

  /**
   * Handles either rejection or confirmation for incoming community request from other
   * user asking to join current user's community
   *
   * @param userId - user ID an invitation has been processed with
   * @param entityId community Id an invitation has been processed with
   */
  protected updateUserRequests(userId: string, entityId: string): void {
    this.reduceUserRequests(userId);
    this.notifyTableDataChanges(UserTableCreators.ENTITY_USERS_REQUESTS, {
      usersRequests: this.userRequests,
      entityId,
      allowManaging: true,
    });
  }

  protected updateUserInvites(userId: string, entityId: string): void {
    this.reduceUserInvites(userId);
    this.notifyTableDataChanges(UserTableCreators.ENTITY_USERS_INVITES, {
      usersRequests: this.invites,
      entityId,
      allowManaging: true,
    });
  }

  /**
   * Handles either rejection or confirmation for incoming request from other user
   * inviting logged in user to join their community
   *
   * @param actorId - user ID an invitation has been processed with
   * @param entityId - community Id an invitation has been processed with
   */
  protected updateUserPersonalInvites(actorId: string, entityId: string): void {
    this.reduceUserPersonalInviteRequests(entityId);
    this.notifyTableDataChanges(UserTableCreators.PERSONAL_USER_INVITES_FOR_COMMUNITY, {
      personalEntities: this.userPersonalInvites,
      actorId,
    });
  }

  /**
   * Handles either rejection or confirmation for incoming request from other user
   * inviting logged in user to join the project
   *
   * @param actorId - user ID an invitation has been processed with
   * @param entityId - project Id an invitation has been processed with
   */
  protected updateUserProjectInvites(actorId: string, entityId: string): void {
    this.reduceUserProjectInviteRequests(entityId);
    this.notifyTableDataChanges(UserTableCreators.PERSONAL_USER_INVITES_FOR_PROJECT, {
      personalEntities: this.userProjectInvites,
      actorId,
    });
  }

  /**
   * Notifies resolverChanges$ the change has happened
   *
   * @param requests {PaginatablePendingUserInvolvements}
   */
  protected notifyResolverChanges(requests: PaginatablePendingUserInvolvements): void {
    this.resolverChanges$.next(requests);
  }

  /**
   * Notifies tableDataChanges$ the change has happened
   *
   * @param type {UserTableCreators} data
   * @param data {TableConstructorDataObject}
   */
  protected notifyTableDataChanges(type: UserTableCreators, data: TableConstructorDataObject): void {
    this.tableDataChanges$.next(TableCreatorFactory.createTableConstructorData(type, data));
  }

  /**
   * Updates the list with community request from other user removing the given user
   *
   * @param userId - user ID to be deleted
   */
  private reduceUserRequests(userId: string): void {
    this.userRequests = this.userRequests.filter((userRequest: PendingUserInvolvement) => {
      return userRequest.user.user_id !== userId;
    });
  }

  private reduceUserInvites(userId: string): void {
    this.invites = this.invites.filter((invite: PendingUserInvolvement) => {
      return invite.user.user_id !== userId;
    });
  }

  /**
   * Updates the list with invitation community request from other user removing the given user
   *
   * @param entityId - entity ID to be deleted
   */
  private reduceUserPersonalInviteRequests(entityId: string): void {
    this.userPersonalInvites = this.userPersonalInvites.filter(
      (userRequestFromCommunity: PendingUserInvolvementInCommunity) => {
        return userRequestFromCommunity.community.entity_id !== entityId;
      },
    );
  }

  /**
   * Updates the list with invitation project request from other user removing the given project
   *
   * @param entityId - project ID to be deleted
   */

  private reduceUserProjectInviteRequests(entityId: string): void {
    this.userProjectInvites = this.userProjectInvites.filter(
      (userRequestFromProject: PendingUserInvolvementInProject) => {
        return userRequestFromProject.project.entity_id !== entityId;
      },
    );
  }
}
